/**
 * The ONE way a decorative mutation reaches the store (DS-6).
 *
 * `decorative-svg-panel.js` and `ui/shell.js`'s + gap handler were two
 * hand-written copies of the same three steps — one `store.batch`, an appended
 * CSS block, one render emit — and the drift that prevents is not hypothetical:
 * DS-2 (the colour token) and DS-6 (the edge) would each have had to be made
 * twice, and DS-6's CSS append changed shape, which is exactly the kind of edit
 * one copy receives and the other does not.
 *
 * Pure over its inputs apart from the store write, so both callers keep their
 * own resolution logic — the panel resolves by instance, the gap handler by
 * page index — and share only the part that was identical.
 */

import { store } from '../core/state.js';
import { eventBus } from '../core/events.js';

/**
 * @param {{path: string, sectionPath: string, section: object}} resolved
 *   `path` is the section's `.html` path; `sectionPath` is its parent, which is
 *   where `.css` hangs off.
 * @param {string} updatedHtml
 * @param {string|null} addedScopedCss — appended, never replacing: someone may
 *   have edited the block, and `missingDecorativeCss` already returns only the
 *   parts this section does not have.
 * @param {string} label — the undo-history entry's name.
 */
export function applyDecorativeMutation(resolved, updatedHtml, addedScopedCss, label) {
  const mutations = [{ path: resolved.path, value: updatedHtml }];
  if (addedScopedCss) {
    const currentCss = resolved.section?.css || '';
    mutations.push({
      path: `${resolved.sectionPath}.css`,
      value: `${currentCss}${currentCss.trim() ? '\n\n' : ''}${addedScopedCss}`
    });
  }
  // ONE batch, so the html and its CSS are one history entry — undoing a shape
  // must not leave the rules it needed behind, or the next insert would find
  // `missingDecorativeCss` satisfied and append nothing.
  store.batch(mutations, label);
  eventBus.emit('preview:render-request', { preserveScroll: true });
}
